import qs from 'qs';
import z from 'zod';
import { ContactType, FindAllContactsFilters, FindContactsDatabaseResponse } from '../app/lib/filters/dto';
import { contactsFiltersAdapter } from '../app/lib/utils';
import { CompanyDTO } from '../app/pages/database/companies/dto';
import { updateCompanySchema } from '../app/pages/database/companies/editCompany/form/validation';
import { PersonDTO } from '../app/pages/database/people/dto';
import { baseApi } from './_baseQuery';
import { JobResponse, PollingResponse } from '../app/lib/polling/dto';


export const companyApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        findAllCompanies: build.query<{ companies: FindContactsDatabaseResponse<ContactType.COMPANY>, toActivate: number }, FindAllContactsFilters>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const companiesResponse = await fetchWithBQ(
                        {
                            url: "/companies",
                            params: qs.stringify({ ...contactsFiltersAdapter(_arg, ContactType.COMPANY) }, {
                                arrayFormat: "repeat",
                                skipNulls: true,
                                allowEmptyArrays: false,
                                encode: true,
                            })
                        }
                    );
                    if (companiesResponse.error) return { error: companiesResponse.error };

                    const companiesToActivateCount = await fetchWithBQ("/companies/count");
                    if (companiesToActivateCount.error) return { error: companiesToActivateCount.error };

                    const toActivateCount = companiesToActivateCount.data as { toActivate: number };

                    return {
                        data: {
                            companies: companiesResponse.data as FindContactsDatabaseResponse<ContactType.COMPANY>,
                            toActivate: toActivateCount.toActivate
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch users or avatars',
                            data: error
                        }
                    };
                }
            },
            providesTags: [{ type: 'Database', id: 'companies' }]
        }),
        findOneCompany: build.query<{ company: CompanyDTO, employees: PersonDTO[] | null }, string>({
            async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const companyRes = await fetchWithBQ(`companies/${id}`);
                    if (companyRes.error) return { error: companyRes.error };

                    const company = companyRes.data as CompanyDTO;

                    let employees: PersonDTO[] | null = null;
                    if (company.employeesId && company.employeesId.length > 0) {
                        const employeePromises = company.employeesId.map(employeeId =>
                            fetchWithBQ(`person/${employeeId}`)
                        );

                        const employeesResults = await Promise.all(employeePromises);

                        employees = employeesResults
                            .filter(result => !result.error)
                            .map(result => result.data as PersonDTO);

                        if (employees.length === 0) {
                            employees = null;
                        }
                    }

                    return {
                        data: {
                            company,
                            employees
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch person or company',
                            data: error
                        }
                    };
                }
            },
            providesTags: (_result, _error, id) => [{ type: 'Companies', id }, ...(_result?.company.employeesId?.map((pid) => ({ type: 'People' as const, id: pid })) ?? [])],
        }),
        updateCompany: build.mutation<{ success: boolean; company: CompanyDTO }, { id: string, data: z.infer<typeof updateCompanySchema> }>({
            query: ({ id, data }) => ({
                url: `/companies/${id}`,
                method: "PUT",
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Companies', id }, { type: 'Database', id: 'companies' }],
        }),
        updateVatCompany: build.mutation<{ success: boolean; company: CompanyDTO }, { id: string, vat: string }>({
            query: ({ id, vat }) => ({
                url: `/companies/${id}/vat-update`,
                method: "PUT",
                body: { vat },
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Companies', id }, { type: 'Database', id: 'companies' }],
        }),
        removeCompany: build.mutation<{ success: boolean; id: string }, string[]>({
            query: (ids) => ({
                url: `companies`,
                method: 'DELETE',
                body: ids
            }),
            invalidatesTags: (_result, _error, _ids) => [{ type: 'Database', id: 'companies' }],
        }),
        restoreCompany: build.mutation<{ success: boolean; }, { ids: string[] }>({
            query: (ids) => ({
                url: `companies/restore`,
                method: 'PUT',
                body: ids
            }),
            invalidatesTags: (_result, _error, _ids) => [{ type: 'Database', id: 'companies' }],
        }),
        removeCompanyBulk: build.mutation<JobResponse, FindAllContactsFilters>({
            query: (filters) => ({
                url: `companies/bulk-delete`,
                method: 'POST',
                params: qs.stringify({ ...contactsFiltersAdapter(filters, ContactType.COMPANY) }, {
                    arrayFormat: "repeat",
                    skipNulls: true,
                    allowEmptyArrays: false,
                    encode: true,
                })
            }),
            invalidatesTags: (_result, _error, _ids) => [{ type: 'Database', id: 'companies' }],
        }),
        importCompaniesFromCSV: build.mutation<JobResponse, FormData>({
            query: (body) => ({
                url: `companies/csv`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Database', id: 'companies' }],
        }),
        getCompaniesCSV: build.mutation<JobResponse, string[]>({
            query: (ids) => ({
                url: `companies/export-csv`,
                method: 'POST',
                body: { ids }
            }),
        }),
        bulkCompaniesCSV: build.mutation<JobResponse, FindAllContactsFilters>({
            query: (filters) => ({
                url: `companies/bulk-csv`,
                method: 'POST',
                params: qs.stringify({ ...contactsFiltersAdapter(filters, ContactType.COMPANY) }, {
                    arrayFormat: "repeat",
                    skipNulls: true,
                    allowEmptyArrays: false,
                    encode: true,
                })
            }),
        }),
    }),
});

export const {
    useFindAllCompaniesQuery,
    useFindOneCompanyQuery,
    useUpdateCompanyMutation,
    useRemoveCompanyMutation,
    useRemoveCompanyBulkMutation,
    useGetCompaniesCSVMutation,
    useBulkCompaniesCSVMutation,
    useRestoreCompanyMutation,
    useImportCompaniesFromCSVMutation,
    useUpdateVatCompanyMutation
} = companyApi